import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserProfile } from '../services/profilesService';
import { listEvents } from '../services/eventsService';
import { db } from '../../sdk_initialization';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, writeBatch, Timestamp } from 'firebase/firestore';
import {getTotalPurchasedCapacityAndAmountByEvent} from '../services/attendeeCapacityService';
import {
  listAllAttendees,
  listAttendeesByVendor,
  listAttendeesByEvent,
  listAttendeesByEventAndVendor,
  addAttendee,
  getAttendee,
  deleteAttendee,
} from '../services/attendeesService';
import { getTotalAllowedAttendees } from '../services/attendeeCapacityService';
import './ProfilePage.css';

const Attendees = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventIdParam = String(searchParams.get('eventId') || '').trim();
  const vendorIdParam = String(searchParams.get('vendorId') || '').trim();
  const returnToParam = String(searchParams.get('returnTo') || '').trim();
  const [userProfile, setUserProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [attendees, setAttendees] = useState([]);
  const [filteredAttendees, setFilteredAttendees] = useState([]);
  
  // Filter states
  const [events, setEvents] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    eventId: '',
    profileId: '',
    vendorNumber: '',
    vendorName: '',
    firstName: '',
    lastName: '',
    yob: '',
    gender: '',
    isPrimary: false,
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Stats
  const [boothsPurchased, setBoothsPurchased] = useState(0);
  const [allowedAttendees, setAllowedAttendees] = useState(0); // Total allowed (free + purchased)
  const [freeAttendeesAllowed, setFreeAttendeesAllowed] = useState(0); // Free attendees only
  const [purchasedAttendees, setPurchasedAttendees] = useState(0); // Purchased additional attendees
  const [currentAttendeeCount, setCurrentAttendeeCount] = useState(0);
  const [additionalCost, setAdditionalCost] = useState(0);
  
  // Refresh trigger
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Additional attendees purchase popup
  const [showBuyAttendeesPopup, setShowBuyAttendeesPopup] = useState(false);
  const [additionalAttendeesCount, setAdditionalAttendeesCount] = useState(1);
  const [processingCheckout, setProcessingCheckout] = useState(false);

  const userRole = String(userProfile?.userType || '').trim();
  const isVendorUser = userRole === 'Vendor';
  const canEditAttendeeFields = userRole === 'Vendor' || userRole === 'Organizer' || userRole === 'EventAdmin' || userRole === 'Admin' || userRole === 'SystemAdmin';
  const backTarget = returnToParam || '/';


  const getCreatedAtMs = (value) => {
    if (!value) return 0;
    if (typeof value?.toDate === 'function') return value.toDate().getTime();
    if (typeof value?.seconds === 'number') return value.seconds * 1000;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getVendorKey = (attendee) =>
    String(
      attendee?.vendorId ||
      attendee?.profileId ||
      attendee?.vendorNumber ||
      attendee?.vendorName ||
      ''
    )
      .trim()
      .toLowerCase();

  const firstDisplayPrimaryIds = useMemo(() => {
    const firstByVendor = new Map();
    const vendorHasPrimary = new Set();
    for (const attendee of filteredAttendees) {
      const vendorKey = getVendorKey(attendee);
      if (!vendorKey) continue;
      if (attendee?.isPrimary === true) {
        vendorHasPrimary.add(vendorKey);
      }

      const existing = firstByVendor.get(vendorKey);
      const attendeeCreatedAtMs = getCreatedAtMs(attendee?.createdAt);
      if (!existing || attendeeCreatedAtMs < existing.createdAtMs) {
        firstByVendor.set(vendorKey, { id: attendee.id, createdAtMs: attendeeCreatedAtMs });
      }
    }

    const fallbackIds = [];
    for (const [vendorKey, info] of firstByVendor.entries()) {
      if (!vendorHasPrimary.has(vendorKey)) {
        fallbackIds.push(info.id);
      }
    }
    return new Set(fallbackIds);
  }, [filteredAttendees]);

  // Load user profile
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentUser?.uid) return;
      try {
        const profile = await getUserProfile(currentUser.uid);
        if (!cancelled) {
          setUserProfile(profile);
          const role = String(profile?.userType || '').trim();
          const adminUser = role === 'Admin' || role === 'SystemAdmin' || role === 'Organizer' || role === 'EventAdmin';
          setIsAdmin(adminUser);
        }
      } catch (e) {
  // Removed console.error
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Load events and vendors
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const evts = await listEvents();
        // Keep existing status behavior for Attendees page
        let filteredEvts = (evts || []).filter(e => e.status === 'Open' || e.status === 'Closed');

        // Apply role-based filtering for event dropdown
        // - SystemAdmin/Admin: all Open/Closed events
        // - EventAdmin: events where UID is in eventAdmins
        // - Organizer: events where UID is in organizer list
        // - Others: none
        if (currentUser && currentUser.uid) {
          try {
            const profile = userProfile || await getUserProfile(currentUser.uid);
            const userType = String(profile?.userType || '').trim();
            const uidLower = String(currentUser.uid || '').trim().toLowerCase();

            if (userType === 'EventAdmin') {
              filteredEvts = filteredEvts.filter(ev =>
                Array.isArray(ev?.eventAdmins) &&
                ev.eventAdmins.some(a => String(a || '').trim().toLowerCase() === uidLower)
              );
            } else if (userType === 'Vendor') {
              const vendorId = String(profile?.vendorId || '').trim();
              if (!vendorId) {
                filteredEvts = [];
              } else {
                const reservationsCol = collection(db, 'boothReservations');
                const vendorReservationsQuery = query(
                  reservationsCol,
                  where('vendorId', '==', vendorId),
                  where('status', '==', 'Reserved')
                );
                const vendorReservationsSnap = await getDocs(vendorReservationsQuery);
                const reservedEventIds = new Set();
                vendorReservationsSnap.forEach(d => {
                  const eventId = d.data()?.eventId;
                  if (eventId) {
                    reservedEventIds.add(String(eventId));
                  }
                });

                filteredEvts = filteredEvts.filter(ev => reservedEventIds.has(String(ev.id)));
              }
            } else if (userType === 'Organizer') {
              filteredEvts = filteredEvts.filter(ev => {
                const organizerList =
                  (Array.isArray(ev?.organizers) && ev.organizers) ||
                  (Array.isArray(ev?.eventOrganizers) && ev.eventOrganizers) ||
                  (Array.isArray(ev?.EventOrganizers) && ev.EventOrganizers) ||
                  (Array.isArray(ev?.eventOrganizerList) && ev.eventOrganizerList) ||
                  (Array.isArray(ev?.EventOrganizerList) && ev.EventOrganizerList) ||
                  [];
                return organizerList.some(a => String(a || '').trim().toLowerCase() === uidLower);
              });
            } else if (userType !== 'Admin' && userType !== 'SystemAdmin') {
              filteredEvts = [];
            }
          } catch (roleErr) {
            filteredEvts = [];
          }
        } else {
          filteredEvts = [];
        }

        if (!cancelled) {
          setEvents(filteredEvts);

          // Apply event filter from URL first; otherwise keep existing behavior.
          const hasEventParam = Boolean(eventIdParam);
          const eventParamVisible = hasEventParam && filteredEvts.some(e => e.id === eventIdParam);
          if (eventParamVisible) {
            setSelectedEvent(eventIdParam);
          } else {
            const stillVisible = filteredEvts.some(e => e.id === selectedEvent);
            if (!stillVisible) {
              setSelectedEvent(filteredEvts.length > 0 ? filteredEvts[0].id : '');
            } else if (filteredEvts.length > 0 && !selectedEvent) {
              setSelectedEvent(filteredEvts[0].id);
            }
          }
        }
        // Only load vendor list for admins
        if (isAdmin) {
          // Get all vendors who have at least one reservation
          const reservationsCol = collection(db, 'boothReservations');
          const reservationsQuery = query(
            reservationsCol,
            where('status', '==', 'Reserved')
          );
          const reservationsSnap = await getDocs(reservationsQuery);

          // Get unique vendorIds from reservations
          const vendorIds = new Set();
          reservationsSnap.forEach(doc => {
            const data = doc.data();
            if (data.vendorId) {
              vendorIds.add(data.vendorId);
            }
          });

          // Load vendors collection to get vendor details
          const vendorsCol = collection(db, 'vendors');
          const vendorsSnap = await getDocs(vendorsCol);
          const vendorsList = [];
          vendorsSnap.forEach(doc => {
            const vendorData = doc.data();
            if (vendorIds.has(doc.id)) {
              vendorsList.push({
                id: doc.id, // Use vendorId
                vendorNumber: vendorData.vendorNumber || doc.id.substring(0, 8),
                vendorName: vendorData.name || vendorData.vendorName || vendorData.businessName || ''
              });
            }
          });

          // Sort by vendorNumber
          vendorsList.sort((a, b) =>
            String(a.vendorNumber).localeCompare(String(b.vendorNumber))
          );

          if (!cancelled) {
            setVendors(vendorsList);
            if (vendorIdParam && vendorsList.some(v => String(v.id) === vendorIdParam)) {
              setSelectedVendor(vendorIdParam);
            }
          }
        } else if (userProfile?.vendorId) {
          // Auto-select vendor for non-admin users
          if (!vendorIdParam || vendorIdParam === userProfile.vendorId) {
            setSelectedVendor(userProfile.vendorId);
          } else {
            setSelectedVendor(userProfile.vendorId);
          }
        }
      } catch (e) {
  // Removed console.error
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, userProfile, currentUser, eventIdParam, vendorIdParam]);

  // Load attendees
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentUser?.uid || !userProfile) return;
      
      setLoading(true);
      try {
        let data = [];
        if (isAdmin) {
          data = await listAllAttendees();
        } else {
          if (userProfile?.vendorId) {
            data = await listAttendeesByVendor(userProfile.vendorId);
          } else {
            data = [];
          }
        }
        if (!cancelled) {
          setAttendees(data);
          setFilteredAttendees(data);
        }
      } catch (e) {
  // Removed console.error
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser, userProfile, isAdmin, refreshTrigger]);

  // Apply filters
  useEffect(() => {
    // Require event selection - show empty if no event selected
    if (!selectedEvent) {
      setFilteredAttendees([]);
      return;
    }
    
    let filtered = [...attendees];
    
    // Filter by selected event (required)
    filtered = filtered.filter(a => a.eventId === selectedEvent);
    
    if (selectedVendor) {
      // For admin, attendees may have profileId; for vendors, use vendorId
      if (isAdmin) {
        filtered = filtered.filter(a => a.profileId === selectedVendor || a.vendorId === selectedVendor);
      } else {
        filtered = filtered.filter(a => a.vendorId === selectedVendor);
      }
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(a => 
        a.firstName?.toLowerCase().includes(term) ||
        a.lastName?.toLowerCase().includes(term) ||
        a.vendorNumber?.toLowerCase().includes(term)
      );
    }

    // Always keep primary attendee first within each vendor list.
    filtered.sort((a, b) => {
      const vendorA = String(a?.vendorId || a?.profileId || '').toLowerCase();
      const vendorB = String(b?.vendorId || b?.profileId || '').toLowerCase();
      if (vendorA !== vendorB) return vendorA.localeCompare(vendorB);

      const aPrimary = a?.isPrimary === true ? 0 : 1;
      const bPrimary = b?.isPrimary === true ? 0 : 1;
      if (aPrimary !== bPrimary) return aPrimary - bPrimary;

      return getCreatedAtMs(a?.createdAt) - getCreatedAtMs(b?.createdAt);
    });
    
    setFilteredAttendees(filtered);
  }, [attendees, selectedEvent, selectedVendor, searchTerm]);

  // Calculate stats when event or vendor changes
  useEffect(() => {
    (async () => {
      if (!selectedEvent || (!isAdmin && !currentUser?.uid)) return;
      try {
        const event = events.find(e => e.id === selectedEvent);
        if (!event) return;
        const freeAttendees = event.freeAttendeesPerBooth || 0;
        const additionalPrice = parseFloat(event.additionalAttendeePrice) || 0;
        const reservationsCol = collection(db, 'boothReservations');

        // For admin, if a vendor is selected, filter stats for that vendor only
        let vendorId = null;
        if (isAdmin && selectedVendor) {
          vendorId = selectedVendor

        } else if (!isAdmin) {
          vendorId = selectedVendor || userProfile?.vendorId || null;
        }

        // Booth count
        let boothCount = 0;
        let purchasedCapacity = 0;
        let totalAllowed = 0;
        let freeAllowed = 0;
        let amountPaid = 0;

        if (vendorId) {
          // Vendor-specific booth count
          const vendorReservationsQuery = query(
            reservationsCol,
            where('eventId', '==', selectedEvent),
            where('vendorId', '==', vendorId),
            where('status', '==', 'Reserved')
          );
          const vendorReservationsSnap = await getDocs(vendorReservationsQuery);
          boothCount = vendorReservationsSnap.size;
          freeAllowed = freeAttendees * boothCount;
          try {
            const capacityData = await getTotalAllowedAttendees(
              vendorId,
              selectedEvent,
              boothCount,
              freeAttendees
            );
            purchasedCapacity = capacityData.purchasedAttendees || 0;
            totalAllowed = capacityData.totalAllowed || freeAllowed;
            amountPaid = purchasedCapacity * (capacityData.pricePerAdditionalAttendeeCents || 0) / 100  ;
          } catch (err) {
            // Removed console.error
            purchasedCapacity = 0;
            totalAllowed = freeAllowed;
            amountPaid = 0;
          }
        } else {
          // All vendors booth count (admin, no vendor selected)
          const allReservationsQuery = query(
            reservationsCol,
            where('eventId', '==', selectedEvent),
            where('status', '==', 'Reserved')
          );
          const allReservationsSnap = await getDocs(allReservationsQuery);
          boothCount = allReservationsSnap.size;
          freeAllowed = freeAttendees * boothCount;
          const totals = await getTotalPurchasedCapacityAndAmountByEvent(selectedEvent);
          purchasedCapacity = totals.totalPurchased;
          totalAllowed = freeAllowed + purchasedCapacity;
          amountPaid = (totals.totalAmountPaidCents || 0) / 100;
        }

        setBoothsPurchased(boothCount);
        setFreeAttendeesAllowed(freeAllowed);
        setPurchasedAttendees(purchasedCapacity);
        setAllowedAttendees(totalAllowed);
        setAdditionalCost(amountPaid);

        // Current attendee count
        let currentCount = 0;
        if (vendorId) {
          currentCount = filteredAttendees.filter(a =>
            a.eventId === selectedEvent &&
            a.vendorId === vendorId
          ).length;
        } else {
          currentCount = filteredAttendees.filter(a => a.eventId === selectedEvent).length;
        }
        setCurrentAttendeeCount(currentCount);
      } catch (e) {
  // Removed console.error
      }
    })();
  }, [selectedEvent, selectedVendor, filteredAttendees, events, isAdmin, currentUser, userProfile]);

  const handleAddNew = async () => {
    // Check if event is selected
    if (!selectedEvent) {
      setFormError('Please select an event first');
      alert('Please select an event before adding attendees.');
      return;
    }
    // Check if the vendor (admin or vendor) has purchased booths for this event
    let vendorIdToCheck = userProfile?.vendorId;
    if (isAdmin && selectedVendor) {
      // Admin flow: get vendorId from selected vendor
      const vendorProfile = await getUserProfile(selectedVendor);
      vendorIdToCheck = vendorProfile?.vendorId || selectedVendor;
    }

    // Block attendee creation when vendor has already checked out for this event.
    try {
      const checkinRef = doc(db, 'checkins', `${selectedEvent}_${vendorIdToCheck || ''}`);
      const checkinSnap = await getDoc(checkinRef);
      const checkinData = checkinSnap.exists() ? checkinSnap.data() : null;
      const vendorCheckedOut = checkinData?.checkedOut === true || (checkinData?.checkedOut == null && Boolean(checkinData?.checkoutAt));
      if (vendorCheckedOut) {
        alert('This vendor is already checked out for the selected event. You cannot add attendees after checkout.');
        return;
      }
    } catch (e) {
      alert('Failed to verify vendor checkout status. Please try again.');
      return;
    }

    try {
      const reservationsCol = collection(db, 'boothReservations');
      const vendorBoothsQuery = query(
        reservationsCol,
        where('eventId', '==', selectedEvent),
        where('vendorId', '==', vendorIdToCheck || ''),
        where('status', '==', 'Reserved')
      );
      const vendorBoothsSnap = await getDocs(vendorBoothsQuery);
      if (vendorBoothsSnap.size === 0) {
        alert('You must purchase at least one booth for this event before adding attendees.');
        return;
      }
      // Check if vendor has reached their attendee limit (free + purchased)
      const currentCount = filteredAttendees.filter(a =>
        a.eventId === selectedEvent &&
        a.vendorId === vendorIdToCheck
      ).length;
      if (currentCount >= allowedAttendees) {
        const purchased = purchasedAttendees || 0;
        alert(`You have reached your attendee limit (${allowedAttendees} total: ${freeAttendeesAllowed} free + ${purchased} purchased for ${boothsPurchased} booth(s)).\n\nTo add more attendees, use the "Buy Additional Attendees" button below.`);
        return;
      }
    } catch (e) {
  // Removed console.error
      alert('Failed to verify booth purchase. Please try again.');
      return;
    }
        
    
    setEditingId(null);
    
    // Get vendor name and number when setting up form
    let vendorName = '';
    let vendorNumber = '';
    const vendorProfileId = isAdmin ? (selectedVendor || currentUser.uid) : currentUser.uid;
    
  
  
  
    
    if (isAdmin && selectedVendor) {
      const vendor = vendors.find(v => v.id === selectedVendor);
  
      vendorName = vendor?.vendorName || '';
      vendorNumber = vendor?.vendorNumber || '';
    } else {
  
      // Look it up from vendors collection
      if (!vendorNumber && userProfile?.vendorId) {
        try {
          const vendorDocRef = doc(db, 'vendors', userProfile.vendorId);
          const vendorDocSnap = await getDoc(vendorDocRef);
          if (vendorDocSnap.exists()) {
            const vendorData = vendorDocSnap.data();
            vendorNumber = vendorData.vendorNumber || '';
            // Also populate vendorName for non-admin users so attendee records include it
            vendorName = vendorData.name || vendorData.vendorName || '';
          }
        } catch (e) {
          // Removed console.error
        }
      }
    }
    
  
    
    setFormData({
      eventId: selectedEvent || '',
      vendorId: isAdmin && selectedVendor
        ? (await getUserProfile(selectedVendor))?.vendorId || selectedVendor
        : userProfile?.vendorId || '',
      createdByProfileId: currentUser?.uid || '',
      vendorNumber: vendorNumber,
      vendorName: vendorName,
      firstName: '',
      lastName: '',
      yob: '',
      gender: '',
      isPrimary: false,
    });
    setFormError('');
    setShowForm(true);
  };

  const handleEdit = (attendee) => {
    setEditingId(attendee.id);
    setFormData({
      eventId: attendee.eventId,
      vendorId: attendee.vendorId,
      createdBy: attendee.createdBy || currentUser?.uid || '',
      vendorNumber: attendee.vendorNumber,
      vendorName: attendee.vendorName || '',
      firstName: attendee.firstName,
      lastName: attendee.lastName,
      yob: attendee.yob,
      gender: attendee.gender,
      isPrimary: attendee?.isPrimary === true,
    });
    setFormError('');
    setShowForm(true);
  };

  const handleDelete = async (attendee) => {
    if (attendee?.isPrimary === true) {
      alert('Primary attendee cannot be deleted. Please select another attendee as Primary before deleting this one. There must always be one Primary attendee.');
      return;
    }

    if (isVendorUser && attendee?.checkedIn === true) {
      alert('Checked-in attendees cannot be deleted by Vendor users. Please contact an Organizer, EventAdmin, or Admin.');
      return;
    }

    const id = attendee?.id;
    if (!id) return;
    if (!window.confirm('Please note that if you have purchased any additional services for this attendee, those will be lost without a refund. Do you really want to proceed with this deletion?')) return;
    
    try {
      await deleteAttendee(id);
      setAttendees(prev => prev.filter(a => a.id !== id));
    } catch (e) {
      const msg = e && e.message ? e.message : '';
      if (msg.includes('primary attendee')) {
        alert('Primary attendee cannot be deleted. Please select another attendee as Primary before deleting this one. There must always be one Primary attendee.');
      } else if (msg.includes('Cannot delete the last attendee')) {
        alert('Primary attendee cannot be deleted. Please select another attendee as Primary before deleting this one. There must always be one Primary attendee.');
      } else {
        alert('Failed to delete attendee: ' + msg);
      }
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const saveEditableAttendeeFieldsDirect = async (attendeeId, updates) => {
    const attendeeRef = doc(db, 'attendees', attendeeId);
    const nextYob = Number(updates?.yob || 0);
    const nextGender = String(updates?.gender || '');
    const nextIsPrimary = updates?.isPrimary === true;
    const now = Timestamp.now();

    if (nextIsPrimary && updates?.eventId && updates?.vendorId) {
      const peersQuery = query(
        collection(db, 'attendees'),
        where('eventId', '==', updates.eventId),
        where('vendorId', '==', updates.vendorId)
      );
      const peersSnap = await getDocs(peersQuery);
      const batch = writeBatch(db);

      peersSnap.forEach((peerDoc) => {
        if (peerDoc.id !== attendeeId && peerDoc.data()?.isPrimary === true) {
          batch.update(peerDoc.ref, {
            isPrimary: false,
            updatedAt: now,
          });
        }
      });

      batch.update(attendeeRef, {
        yob: nextYob,
        gender: nextGender,
        isPrimary: true,
        updatedAt: now,
      });
      await batch.commit();
    } else {
      await updateDoc(attendeeRef, {
        yob: nextYob,
        gender: nextGender,
        isPrimary: nextIsPrimary,
        updatedAt: now,
      });
    }

    return {
      yob: nextYob,
      gender: nextGender,
      isPrimary: nextIsPrimary,
      updatedAt: now,
    };
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    // Validation
    if (!formData.eventId) {
      setFormError('Please select an event');
      return;
    }
    // For vendors, vendorId is set automatically; only admins need to select a vendor
    if (isAdmin && !formData.vendorId) {
      setFormError('Please select a vendor');
      return;
    }
    if (!formData.firstName.trim()) {
      setFormError('First name is required');
      return;
    }
    if (!formData.lastName.trim()) {
      setFormError('Last name is required');
      return;
    }
    if (!formData.yob || formData.yob < 1900 || formData.yob > new Date().getFullYear()) {
      setFormError('Please enter a valid year of birth');
      return;
    }
    if (!formData.gender) {
      setFormError('Please select a gender');
      return;
    }
    
    // Check if the vendor has purchased booths for this event (only when adding new, not editing)
    if (!editingId) {
      try {
        const reservationsCol = collection(db, 'boothReservations');

        // Resolve which vendorId to check for reserved booths.
        // Previously this used `userProfile?.vendorId` (the admin's vendor),
        // which caused admins adding attendees for another vendor to miss booths.
        let vendorIdToCheck = '';
        if (isAdmin) {
          // Prefer an explicit vendorId in the form data
          if (formData.vendorId) {
            vendorIdToCheck = formData.vendorId;
          } else if (formData.profileId) {
            // `profileId` for admins may be a profile id; try to resolve to vendorId
            try {
              const vp = await getUserProfile(formData.profileId);
              vendorIdToCheck = vp?.vendorId || formData.profileId;
            } catch (err) {
              vendorIdToCheck = formData.profileId;
            }
          }
        } else {
          vendorIdToCheck = userProfile?.vendorId || '';
        }

        // Block attendee creation when vendor has already checked out for this event.
        const checkinRef = doc(db, 'checkins', `${formData.eventId}_${vendorIdToCheck || ''}`);
        const checkinSnap = await getDoc(checkinRef);
        const checkinData = checkinSnap.exists() ? checkinSnap.data() : null;
        const vendorCheckedOut = checkinData?.checkedOut === true || (checkinData?.checkedOut == null && Boolean(checkinData?.checkoutAt));
        if (vendorCheckedOut) {
          setFormError('This vendor is already checked out for the selected event. You cannot add attendees after checkout.');
          setSaving(false);
          return;
        }

        // Enforce uniqueness: firstName + lastName must be unique for the same vendor.
        const normalizedVendorId = String(vendorIdToCheck || '').trim().toLowerCase();
        const normalizedFirst = String(formData.firstName || '').trim().toLowerCase();
        const normalizedLast = String(formData.lastName || '').trim().toLowerCase();
        const duplicateNameForVendor = (attendees || []).some(a => {
          const aVendor = String(a?.vendorId || '').trim().toLowerCase();
          const aFirst = String(a?.firstName || '').trim().toLowerCase();
          const aLast = String(a?.lastName || '').trim().toLowerCase();
          return aVendor === normalizedVendorId && aFirst === normalizedFirst && aLast === normalizedLast;
        });
        if (duplicateNameForVendor) {
          setFormError('An attendee with the same first and last name already exists for this vendor.');
          setSaving(false);
          return;
        }

        const vendorBoothsQuery = query(
          reservationsCol,
          where('eventId', '==', formData.eventId),
          where('vendorId', '==', vendorIdToCheck || ''),
          where('status', '==', 'Reserved')
        );
        const vendorBoothsSnap = await getDocs(vendorBoothsQuery);
        
  
        
        if (vendorBoothsSnap.size === 0) {
          const selectedEvent = events.find(e => e.id === formData.eventId);
          setFormError(`No booths found for this vendor in ${selectedEvent?.name || 'this event'}. Please ensure booths are purchased before adding attendees.`);
          setSaving(false);
          return;
        }
        
        // For non-admin vendors, check if they've reached their attendee limit (free + purchased)
        if (!isAdmin || formData.profileId === currentUser.uid) {
          // Get the event to calculate allowed attendees
          const event = events.find(e => e.id === formData.eventId);
          if (event) {
            const freeAttendees = event.freeAttendeesPerBooth || 0;
            const freeAllowed = freeAttendees * vendorBoothsSnap.size;
            
            // Get vendorId for capacity check
            let vendorIdForCapacity = null;
            if (formData.profileId === currentUser.uid) {
              // Current user - use their vendorId from profile
              vendorIdForCapacity = userProfile?.vendorId || null;
            } else if (isAdmin) {
              // Admin adding for another vendor - need to get their vendorId
              try {
                const vendorProfile = await getUserProfile(formData.profileId);
                vendorIdForCapacity = vendorProfile?.vendorId || null;
              } catch (err) {
                // Removed console.error
              }
            }
            
            // Get purchased additional capacity from attendeeCapacity collection
            let totalAllowed = freeAllowed;
            if (vendorIdForCapacity) {
              try {
                const capacityData = await getTotalAllowedAttendees(
                  vendorIdForCapacity,
                  formData.eventId,
                  vendorBoothsSnap.size,
                  freeAttendees
                );
                totalAllowed = capacityData.totalAllowed || freeAllowed;
              } catch (err) {
                // Removed console.error
                // Fall back to free attendees only if capacity check fails
              }
            }
            
            // Count current attendees for this vendor/event (excluding the one being edited if applicable)
            const currentCount = attendees.filter(a => 
              a.eventId === formData.eventId && 
              a.vendorId === vendorIdForCapacity &&
              a.id !== editingId
            ).length;
            
            if (currentCount >= totalAllowed) {
              setFormError(`Attendee limit reached (${totalAllowed} attendees allowed: ${freeAllowed} free + ${totalAllowed - freeAllowed} purchased for ${vendorBoothsSnap.size} booth(s)). To add more attendees, purchase additional attendee capacity.`);
              setSaving(false);
              return;
            }
          }
        }
      } catch (e) {
  // Removed console.error
        setFormError('Failed to verify booth purchase. Please try again.');
        setSaving(false);
        return;
      }
    }
    
    setSaving(true);
    try {
            

      // Get price paid for additional attendee (in cents)
      let pricePaidCents = 0;
      const event = events.find(e => e.id === formData.eventId);
      if (event && event.additionalAttendeePrice) {
        let price = event.additionalAttendeePrice;
        if (price > 10) {
          price = Math.round(price); // assume already in cents
        } else {
          price = Math.round(price * 100); // convert dollars to cents
        }
        pricePaidCents = price;
      }

      if (editingId) {
        if (!canEditAttendeeFields) {
          setFormError('You do not have permission to edit attendee details.');
          setSaving(false);
          return;
        }

        const updatedFields = await saveEditableAttendeeFieldsDirect(editingId, {
          eventId: formData.eventId,
          vendorId: formData.vendorId,
          yob: formData.yob,
          gender: formData.gender,
          isPrimary: formData.isPrimary === true,
        });

        setAttendees(prev => prev.map(a => {
          if (a.id === editingId) {
            return {
              ...a,
              ...updatedFields,
            };
          }

          if (
            updatedFields.isPrimary &&
            a?.eventId === formData.eventId &&
            a?.vendorId === formData.vendorId
          ) {
            return {
              ...a,
              isPrimary: false,
            };
          }

          return a;
        }));
      } else {
        // Ensure vendorId and createdByProfileId are set
        const attendeeData = {
          ...formData,
          vendorId: formData.vendorId || (isAdmin && selectedVendor
            ? (await getUserProfile(selectedVendor))?.vendorId || selectedVendor
            : userProfile?.vendorId || ''),
          createdByProfileId: currentUser?.uid || '',
          pricePaidCents,
        };
        const hasExistingForVendorEvent = (attendees || []).some(a =>
          a?.eventId === attendeeData.eventId && a?.vendorId === attendeeData.vendorId
        );
        attendeeData.isPrimary = !hasExistingForVendorEvent;
        const id = await addAttendee(attendeeData);
        const createdAttendee = await getAttendee(id).catch(() => null);
        setAttendees(prev => [{ id, ...(createdAttendee || attendeeData) }, ...prev]);
      }

  

      setShowForm(false);
      setEditingId(null);
      setFormData({
        eventId: '',
        profileId: '',
        vendorNumber: '',
        vendorName: '',
        firstName: '',
        lastName: '',
        yob: '',
        gender: '',
        isPrimary: false,
      });
    } catch (e) {
      console.error('Failed to save attendee:', e);
      setFormError('Failed to save attendee: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleVendorChange = async (e) => {
    const vendorId = e.target.value;
    setSelectedVendor(vendorId);
    
    
    
    if (vendorId) {
      const vendor = vendors.find(v => v.id === vendorId);
      
      
      if (vendor) {
        const updates = {
          profileId: vendorId,
          vendorNumber: vendor.vendorNumber || '',
          vendorName: vendor.vendorName || '',
        };
        
        
        setFormData(prev => ({
          ...prev,
          ...updates
        }));
      }
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    setRefreshTrigger(prev => prev + 1);
  };

  const getVendorIdForSelectedContext = useMemo(() => {
    if (isAdmin) {
      return String(selectedVendor || '').trim();
    }
    return String(userProfile?.vendorId || '').trim();
  }, [isAdmin, selectedVendor, userProfile?.vendorId]);

  const selectedVendorDisplayName = useMemo(() => {
    const vendorId = String(getVendorIdForSelectedContext || '').trim();
    if (!vendorId) return '';
    const row = (vendors || []).find(v => String(v?.id || '') === vendorId);
    if (row?.vendorName) return row.vendorName;
    if (String(userProfile?.vendorId || '').trim() === vendorId) {
      return userProfile?.businessName || userProfile?.vendorName || '';
    }
    return '';
  }, [getVendorIdForSelectedContext, vendors, userProfile?.vendorId, userProfile?.businessName, userProfile?.vendorName]);

  const isVendorCheckedOutForEvent = async (eventId, vendorId) => {
    if (!eventId || !vendorId) return false;
    const checkinRef = doc(db, 'checkins', `${eventId}_${vendorId}`);
    const checkinSnap = await getDoc(checkinRef);
    if (!checkinSnap.exists()) return false;
    const checkinData = checkinSnap.data() || {};
    return checkinData.checkedOut === true || (checkinData.checkedOut == null && Boolean(checkinData.checkoutAt));
  };

  const handleBuyAdditionalAttendees = async () => {
    if (!selectedEvent) {
      alert('Please select an event first.');
      return;
    }

    const vendorId = getVendorIdForSelectedContext;
    if (!vendorId) {
      alert('Please select a vendor first.');
      return;
    }

    try {
      const vendorCheckedOut = await isVendorCheckedOutForEvent(selectedEvent, vendorId);
      if (vendorCheckedOut) {
        alert('This vendor is already checked out for the selected event. You cannot buy additional attendees after checkout.');
        return;
      }
    } catch (e) {
      alert('Failed to verify vendor checkout status. Please try again.');
      return;
    }

    setAdditionalAttendeesCount(1);
    setShowBuyAttendeesPopup(true);
  };

  const handleProceedToCheckout = async () => {
    const vendorId = getVendorIdForSelectedContext;
    if (!vendorId) {
      alert('Please select a vendor first.');
      return;
    }

    try {
      const vendorCheckedOut = await isVendorCheckedOutForEvent(selectedEvent, vendorId);
      if (vendorCheckedOut) {
        alert('This vendor is already checked out for the selected event. You cannot buy additional attendees after checkout.');
        setShowBuyAttendeesPopup(false);
        return;
      }
    } catch (e) {
      alert('Failed to verify vendor checkout status. Please try again.');
      return;
    }

    const event = events.find(e => e.id === selectedEvent);
    if (!event) {
      alert('Event not found.');
      return;
    }

    // Convert price from cents to dollars if needed
    let price = event.additionalAttendeePrice || 0;
    if (price > 10) {
      price = price / 100;
    }
    const totalAmount = price * additionalAttendeesCount;

    if (totalAmount <= 0) {
      alert('Invalid amount. Please check the event pricing.');
      return;
    }

    // Set loading state and lock cursor
    setProcessingCheckout(true);

    // Convert to cents for Stripe
    const amountInCents = Math.round(totalAmount * 100);

    // Get vendor details from vendors collection to ensure metadata uses business values.
    const getVendorDetails = async () => {
      let vendorNum = '';
      let vendorDisplayName = '';

      const selectedVendorRow = (vendors || []).find(v => String(v?.id || '') === String(vendorId || ''));
      if (selectedVendorRow) {
        vendorNum = selectedVendorRow.vendorNumber || vendorNum;
        vendorDisplayName = selectedVendorRow.vendorName || vendorDisplayName;
      }

      if (!vendorDisplayName && String(userProfile?.vendorId || '') === String(vendorId || '')) {
        vendorNum = userProfile?.vendorNumber || vendorNum;
        vendorDisplayName = userProfile?.businessName || userProfile?.vendorName || vendorDisplayName;
      }

      if (!vendorDisplayName && selectedVendorDisplayName) {
        vendorDisplayName = selectedVendorDisplayName;
      }

      if (vendorId) {
        try {
          const vendorDocRef = doc(db, 'vendors', vendorId);
          const vendorDocSnap = await getDoc(vendorDocRef);
          if (vendorDocSnap.exists()) {
            const vendorData = vendorDocSnap.data();
            vendorNum = vendorNum || vendorData.vendorNumber || '';
            vendorDisplayName =
              vendorDisplayName ||
              vendorData.name ||
              vendorData.vendorName ||
              vendorData.businessName ||
              '';
          }
        } catch (e) {
          console.error('[Attendees] Failed to load vendor details:', e);
        }
      }

      return {
        vendorNum,
        vendorDisplayName: vendorDisplayName || vendorId || 'Vendor',
      };
    };

    try {
      const { vendorNum, vendorDisplayName } = await getVendorDetails();
      const confirmed = window.confirm(
        `Confirm purchase of ${additionalAttendeesCount} additional attendee(s) for ${vendorDisplayName}?`
      );
      if (!confirmed) {
        setProcessingCheckout(false);
        return;
      }

      const payeeName =
        userProfile?.name ||
        `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim() ||
        currentUser?.displayName ||
        'Vendor';
      // Create dynamic description with environment prefix (matching booth purchase format)
      const env = import.meta.env.VITE_APP_ENV || 'development';
      let envPrefix = '';
      if (env === 'dev') {
        envPrefix = 'DEV: ';
      } else if (env === 'test') {
        envPrefix = 'TST: ';
      }

      
      
      
      // Production has no prefix
      const description = `${envPrefix}${event.name}-BAZAAR-${vendorDisplayName}-Attendee-Count ${additionalAttendeesCount}`;
      
      // Navigate to checkout with attendee purchase details
      navigate('/checkout', {
        state: {
          amount: amountInCents, // Stripe expects cents
          description: description,
          eventName: event.name,
          eventId: event.id,
          booths: [], // Empty array for non-booth purchases
          paymentWaitMinutes: event.paymentWait, // Pass paymentWait from event
          metadata: {
            purchaseType: 'attendees',
            eventId: event.id,
            eventName: event.name,
            attendeeCount: additionalAttendeesCount,
            pricePerAttendee: price, // price in dollars
            vendorId: vendorId,
            vendorNumber: vendorNum,
            vendorName: vendorDisplayName,
            payeeEmail: currentUser?.email || '',
            payeePhone: userProfile?.phone || '',
            payeeName,
            transactionAmount: totalAmount // pass total in dollars for transaction record
          }
        }
      });
    } catch (error) {
      console.error('[Attendees] Error proceeding to checkout:', error);
      setProcessingCheckout(false);
      alert('Failed to proceed to checkout. Please try again.');
    }
  };

  if (loading) {
    return <div style={{ padding: 24 }}>Loading...</div>;
  }

  return (
    <div className="profile-container profile-wide-container">
      <div className="profile-header-row">
        <button type="button" className="back-btn" onClick={() => navigate(backTarget)}>← Back</button>
        <div style={{ flex: 1 }} />
        <button 
          type="button" 
          className="apply-btn" 
          onClick={handleRefresh} 
          style={{ marginRight: 8, minWidth: '120px' }}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : '🔄 Refresh'}
        </button>
        <button type="button" className="cancel-btn" onClick={() => navigate(backTarget)}>Close</button>
      </div>
      <h2 className="profile-title">Event Attendees</h2>
      {/* Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isAdmin ? '1fr 1fr 1fr' : '1fr 1fr',
        gap: 16,
        marginBottom: 24,
        padding: 16,
        backgroundColor: '#f9fafb',
        borderRadius: 8,
      }}>
        <div>
          <label className="profile-label">Event *</label>
          <select
            className="profile-input"
            value={selectedEvent}
            onChange={(e) => setSelectedEvent(e.target.value)}
            required
          >
            <option value="">Select an Event</option>
            {events.map(e => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
        </div>
        {/* Only show vendor dropdown for admins */}
        {isAdmin && (
          <div>
            <label className="profile-label">Vendor</label>
            <select
              className="profile-input"
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
            >
              <option value="">All Vendors</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>
                  {v.vendorNumber} - {v.vendorName}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="profile-label">Search</label>
          <input
            type="text"
            className="profile-input"
            placeholder="Search by name or vendor number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      {!isAdmin && String(userProfile?.userType || '').trim() === 'Vendor' && events.length === 0 && (
        <div
          style={{
            marginBottom: 24,
            padding: 12,
            borderRadius: 8,
            border: '1px solid #fcd34d',
            backgroundColor: '#fffbeb',
            color: '#92400e',
            fontWeight: 500,
          }}
        >
          No events available until at least one booth is reserved.
        </div>
      )}
      {/* Stats */}
      {selectedEvent && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: 12,
          marginBottom: 24,
        }}>
          <div style={{
            padding: 12,
            backgroundColor: '#faf5ff',
            borderRadius: 8,
            border: '1px solid #d8b4fe',
          }}>
            <div style={{ fontSize: 11, color: '#7c3aed', marginBottom: 4, fontWeight: 600 }}>
              Booths
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed' }}>{boothsPurchased}</div>
          </div>
          <div style={{
            padding: 12,
            backgroundColor: '#eff6ff',
            borderRadius: 8,
            border: '1px solid #bfdbfe',
          }}>
            <div style={{ fontSize: 11, color: '#1e40af', marginBottom: 4, fontWeight: 600 }}>Free Allowed</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1e40af' }}>{freeAttendeesAllowed}</div>
          </div>
          <div style={{
            padding: 12,
            backgroundColor: '#faf5ff',
            borderRadius: 8,
            border: '1px solid #e9d5ff',
          }}>
            <div style={{ fontSize: 11, color: '#7c3aed', marginBottom: 4, fontWeight: 600 }}>Purchased</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed' }}>{purchasedAttendees}</div>
          </div>
          <div style={{
            padding: 12,
            backgroundColor: '#f0fdf4',
            borderRadius: 8,
            border: '1px solid #bbf7d0',
          }}>
            <div style={{ fontSize: 11, color: '#15803d', marginBottom: 4, fontWeight: 600 }}>Current</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#15803d' }}>{currentAttendeeCount}</div>
          </div>
          <div style={{
            padding: 12,
            backgroundColor: '#ede9fe',
            borderRadius: 8,
            border: '1px solid #c4b5fd',
          }}>
            <div style={{ fontSize: 11, color: '#6b21a8', marginBottom: 4, fontWeight: 600 }}>Total Allowed</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#6b21a8' }}>
              {allowedAttendees}
            </div>
          </div>
          <div style={{
            padding: 12,
            backgroundColor: '#fef2f2',
            borderRadius: 8,
            border: '1px solid #fecaca',
          }}>
            <div style={{ fontSize: 11, color: '#991b1b', marginBottom: 4, fontWeight: 600 }}>Amount Paid</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#991b1b' }}>${additionalCost.toFixed(2)}</div>
          </div>
        </div>
      )}
      {/* Add/Edit Form */}
      {showForm && (
        <div style={{
          marginBottom: 24,
          padding: 24,
          backgroundColor: '#f0fdf4',
          borderRadius: 8,
          border: '2px solid #059669',
        }}>
          <h3 style={{ marginTop: 0, marginBottom: 16 }}>
            {editingId ? 'Edit Attendee' : 'Add New Attendee'}
          </h3>
          
          {formError && (
            <div className="profile-error" style={{ marginBottom: 16 }}>{formError}</div>
          )}
          
          <form onSubmit={handleFormSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label className="profile-label">Event *</label>
                <select
                  name="eventId"
                  className="profile-input"
                  value={formData.eventId}
                  onChange={handleFormChange}
                  required
                  disabled={saving || !isAdmin}
                >
                  <option value="">Select Event</option>
                  {events.map(e => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
              </div>
              
              {isAdmin && (
                <div>
                  <label className="profile-label">Vendor *</label>
                  {isAdmin ? (
                    <select
                      className="profile-input"
                      value={formData.profileId}
                      onChange={handleVendorChange}
                      required
                      disabled={saving}
                    >
                      <option value="">Select Vendor</option>
                      {vendors.map(v => (
                        <option key={v.id} value={v.id}>
                          {v.vendorNumber} - {v.vendorName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="profile-input"
                      value={vendors.find(v => v.id === userProfile?.vendorId)?.vendorName || ''}
                      disabled
                    />
                  )}
                </div>
              )}
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label className="profile-label">First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  className="profile-input"
                  value={formData.firstName}
                  onChange={handleFormChange}
                  required
                  disabled={saving || Boolean(editingId)}
                />
              </div>
              
              <div>
                <label className="profile-label">Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  className="profile-input"
                  value={formData.lastName}
                  onChange={handleFormChange}
                  required
                  disabled={saving || Boolean(editingId)}
                />
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label className="profile-label">Year of Birth *</label>
                <input
                  type="number"
                  name="yob"
                  className="profile-input"
                  value={formData.yob}
                  onChange={handleFormChange}
                  min="1900"
                  max={new Date().getFullYear()}
                  required
                  disabled={saving}
                  placeholder="YYYY"
                />
              </div>
              
              <div>
                <label className="profile-label">Gender *</label>
                <select
                  name="gender"
                  className="profile-input"
                  value={formData.gender}
                  onChange={handleFormChange}
                  required
                  disabled={saving}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </div>

            {editingId && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    name="isPrimary"
                    checked={formData.isPrimary === true}
                    onChange={handleFormChange}
                    disabled={saving || !canEditAttendeeFields}
                  />
                  <span>Primary Attendee</span>
                </label>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="submit"
                className="save-btn"
                disabled={saving}
                style={{ backgroundColor: '#059669' }}
              >
                {saving ? 'Saving...' : editingId ? 'Update Attendee' : 'Add Attendee'}
              </button>
              <button
                type="button"
                className="cancel-btn"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setFormError('');
                }}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      {/* Add Attendee Button */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <button 
          type="button" 
          className="apply-btn" 
          onClick={handleAddNew}
          style={{ 
            fontSize: '16px',
            fontWeight: 600,
            padding: '12px 24px'
          }}
        >
          + Add Attendee
        </button>
        <button
          type="button"
          className="apply-btn"
          onClick={handleBuyAdditionalAttendees}
          style={{
            fontSize: '16px',
            fontWeight: 600,
            padding: '12px 24px',
            backgroundColor: '#a21caf',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer'
          }}
        >
          Buy Additional Attendees
        </button>
      </div>
      {/* Buy Additional Attendees Popup */}
      {showBuyAttendeesPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 32,
            minWidth: 340,
            boxShadow: '0 2px 16px rgba(0,0,0,0.15)',
            position: 'relative',
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 16 }}>Buy Additional Attendees</h3>
            <div style={{ marginBottom: 12, color: '#374151', fontSize: 14 }}>
              Vendor: <strong>{selectedVendorDisplayName || getVendorIdForSelectedContext || '—'}</strong>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Number of Attendees</label>
              <input
                type="number"
                min={1}
                max={100}
                value={additionalAttendeesCount}
                onChange={e => setAdditionalAttendeesCount(Math.max(1, Number(e.target.value)))}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  borderRadius: 6,
                  border: '1px solid #ddd',
                  marginBottom: 8,
                }}
                disabled={processingCheckout}
              />
            </div>
            <div style={{ marginBottom: 16, color: '#6b21a8', fontWeight: 600 }}>
              {(() => {
                const event = events.find(e => e.id === selectedEvent);
                // Convert price from cents to dollars if price is in cents
                let price = event?.additionalAttendeePrice || 0;
                if (price > 10) {
                  price = price / 100;
                }
                return `Total: $${(price * additionalAttendeesCount).toFixed(2)}`;
              })()}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                className="apply-btn"
                onClick={handleProceedToCheckout}
                style={{
                  backgroundColor: '#059669',
                  color: '#fff',
                  fontWeight: 600,
                  padding: '10px 24px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  minWidth: 120,
                  fontSize: '16px',
                  opacity: processingCheckout ? 0.7 : 1,
                }}
                disabled={processingCheckout}
              >
                {processingCheckout ? 'Processing...' : 'Proceed to Checkout'}
              </button>
              <button
                type="button"
                className="cancel-btn"
                onClick={() => {
                  setShowBuyAttendeesPopup(false);
                  setProcessingCheckout(false);
                }}
                style={{
                  padding: '10px 24px',
                  borderRadius: 6,
                  fontSize: '16px',
                  minWidth: 100,
                }}
                disabled={processingCheckout}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Attendees Table */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: 8,
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>Event</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>Vendor</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>First Name</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>Last Name</th>
                <th style={{ padding: 12, textAlign: 'center', fontWeight: 600 }}>YOB</th>
                <th style={{ padding: 12, textAlign: 'center', fontWeight: 600 }}>Gender</th>
                <th style={{ padding: 12, textAlign: 'center', fontWeight: 600 }}>Checked In</th>
                <th style={{ padding: 12, textAlign: 'center', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAttendees.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
                    {!selectedEvent 
                      ? 'Please select an event to view attendees.' 
                      : 'No attendees found. Click "Add Attendee" to get started.'}
                  </td>
                </tr>
              ) : (
                filteredAttendees.map(attendee => {
                  const event = events.find(e => e.id === attendee.eventId);
                  const vendor = vendors.find(v => v.id === attendee.profileId);
                  const isDisplayPrimary = attendee?.isPrimary === true || firstDisplayPrimaryIds.has(attendee.id);
                  const isDeleteBlockedForVendor = isVendorUser && attendee?.checkedIn === true;
                  return (
                    <tr key={attendee.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: 12 }}>{event?.name || attendee.eventId}</td>
                      <td style={{ padding: 12 }}>
                        {attendee.vendorNumber}
                        {(attendee.vendorName || vendor?.vendorName ) && 
                          ` - ${attendee.vendorName || vendor?.vendorName}`}
                      </td>
                      <td style={{ padding: 12 }}>{attendee.firstName}</td>
                      <td style={{ padding: 12 }}>
                        {attendee.lastName}
                        {isDisplayPrimary && (
                          <span
                            title="Primary attendee record"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              marginLeft: 8,
                              padding: '2px 8px',
                              borderRadius: 999,
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                              fontSize: 11,
                              fontWeight: 600,
                              verticalAlign: 'middle',
                            }}
                          >
                            <span aria-hidden="true">★</span>
                            Primary
                          </span>
                        )}
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>{attendee.yob}</td>
                      <td style={{ padding: 12, textAlign: 'center' }}>{attendee.gender}</td>
                      <td style={{ padding: 12, textAlign: 'center' }}>{attendee?.checkedIn === true ? 'Yes' : 'No'}</td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        {canEditAttendeeFields && (
                          <button
                            onClick={() => handleEdit(attendee)}
                            style={{
                              padding: '4px 12px',
                              fontSize: 12,
                              backgroundColor: '#2563eb',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              marginRight: 8,
                            }}
                          >
                            Edit
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(attendee)}
                          disabled={isDeleteBlockedForVendor}
                          title={isDeleteBlockedForVendor ? 'Checked-in attendees can only be deleted by Organizer, EventAdmin, or Admin.' : ''}
                          style={{
                            padding: '4px 12px',
                            fontSize: 12,
                            backgroundColor: isDeleteBlockedForVendor ? '#9ca3af' : '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            cursor: isDeleteBlockedForVendor ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )
            }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Attendees;
